import { FC, lazy, Suspense, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import TextBuildingBlock, { ICreateTextBuildingBlockDto, IUpdateTextBuildingBlockDto } from "models/TextBuildingBlock"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import CustomError from "models/CustomError"
import { IApiLink, IValidationRule } from "@/types"
import ValidationService, { validationRules } from "services/ValidationService"
import toastMessages from "stores/toastMessages"
import { ErrorCode, INPUT_MAX_LENGTH_LARGE, TextVariable, TextVariableGroup } from "config/constants"
import { Grid } from "components/Grid"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import Input from "components/Input/Input"
import FormField from "components/FormField/FormField"
import type { WysiwygEditorRef } from "components/WysiwygEditor/WysiwygEditor"
import { insertInputTextAtCursor } from "components/Input/utils"
import InsertTextVariableDropdown from "components/InsertTextVariableDropdown/InsertTextVariableDropdown"
import { Flex } from "components/Flex"

/**
 * Lazy-loaded so CKEditor 5 (the heaviest dependency) ships as its own on-demand
 * chunk instead of in the eager `main` bundle — it loads only when a rich-text
 * editing modal is opened.
 */
const WysiwygEditor = lazy(() => import("components/WysiwygEditor/WysiwygEditor"))

interface ITextBuildingBlockModalProps {
    textBuildingBlock?: TextBuildingBlock
    textFormatting?: boolean
    onCreate?(data: ICreateTextBuildingBlockDto): Promise<void>
    onUpdate?(updateLink: IApiLink, data: IUpdateTextBuildingBlockDto): Promise<void>
    onClose(): void
}

const TextBuildingBlockModal: FC<ITextBuildingBlockModalProps> = ({
    textBuildingBlock,
    textFormatting: textFormattingProp,
    onCreate,
    onUpdate,
    onClose,
}) => {
    const { t } = useTranslation()
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const wysiwygEditorRef = useRef<WysiwygEditorRef | null>(null)
    const isEdit = !!textBuildingBlock
    const [textBlockId, setTextBlockId] = useState(textBuildingBlock?.textBlockId || "")
    const [text, setText] = useState(textBuildingBlock?.text || "")
    const textFormatting = textBuildingBlock ? textBuildingBlock.textFormatting : (textFormattingProp || false)
    const [error, setError] = useState("")
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const shouldShowDataLossWarning = textBuildingBlock
        ? (textBlockId !== textBuildingBlock.textBlockId || text !== textBuildingBlock.text)
        : !!(textBlockId || text)

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        const validationFields: [string, IValidationRule, any][] = [
            ["textBlockId", validationRules.textBuildingBlock.textBlockId, textBlockId],
            ["text", validationRules.textBuildingBlock.text, text],
        ]
        return ValidationService.validateMultiple(validationFields)
    }, [textBlockId, text])

    async function handleSave() {
        setIsSubmitted(true)

        const textBlockIdTrimmed = textBlockId.trim()
        const textTrimmed = text.trimEnd() // trim only end to save some possible formatting

        if (Object.keys(validationErrors).length > 0
            || !textBlockIdTrimmed
            || !textTrimmed) return

        setIsSaving(true)
        setError("")
        try {
            if (isEdit) {
                if (onUpdate && textBuildingBlock.linkUpdate) {
                    await onUpdate(textBuildingBlock.linkUpdate, {
                        textBlockId: textBlockIdTrimmed,
                        text: textTrimmed,
                    })
                    onClose()
                } else {
                    toastMessages.showSystemError()
                    return
                }
            } else {
                if (onCreate) {
                    await onCreate({
                        textBlockId: textBlockIdTrimmed,
                        text: textTrimmed,
                        textFormatting,
                    })
                } else {
                    toastMessages.showSystemError()
                    return
                }
            }
        } catch (e) {
            if (e instanceof CustomError && (e as CustomError).code === ErrorCode.conflict) {
                setError(t("validation.valueAlreadyDefined"))
            } else {
                toastMessages.showSystemError()
            }
        }
        setIsSaving(false)
    }

    function handleTextBlockIdChange(value: string) {
        setTextBlockId(value)
        setError("")
    }

    function handleInsertTextVariable(variable: TextVariable) {
        if (textFormatting) {
            wysiwygEditorRef.current?.insertText(variable)
            return
        }
        if (textareaRef.current) {
            insertInputTextAtCursor(textareaRef.current, variable)
        }
    }

    return (
        <ConfirmModal
            title={textBuildingBlock
                ? t("textBuildingBlocks.editTextBuildingBlock")
                : t("textBuildingBlocks.addTextBuildingBlock")
            }
            maxWidth="952px"
            onClose={onClose}
            onConfirm={handleSave}
            confirmButtonLabel={t("common.save")}
            dataLossWarning={shouldShowDataLossWarning}
            withWysiwygEditor={textFormatting}>
            <Grid columns="18.3rem auto" alignItems="flex-end">
                <FormField label={t("textBuildingBlocks.id")}>
                    <Input
                        value={textBlockId}
                        onChange={e => handleTextBlockIdChange(e.target.value)}
                        disabled={isSaving}
                        maxLength={40}
                        isInvalid={isSubmitted && (!!error || ValidationService.isFieldInvalid(validationErrors, "textBlockId"))}
                    />
                </FormField>
                <Flex justifyContent="flex-end" className="pb-1">
                    <InsertTextVariableDropdown
                        onTextVariableSelect={handleInsertTextVariable}
                        textVariableGroup={TextVariableGroup.withInvoiceCounter}
                    />
                </Flex>
            </Grid>
            {isSubmitted && (!!error || ValidationService.hasFieldErrorMessage(validationErrors, "textBlockId")) &&
                <ValidationMessage>{error || validationErrors.textBlockId?.message}</ValidationMessage>
            }
            <div className="mt-3">
                {textFormatting
                    ? <Suspense fallback={null}>
                        <WysiwygEditor
                            ref={wysiwygEditorRef}
                            content={text}
                            onChange={setText}
                        />
                    </Suspense>
                    : <Input
                        ref={textareaRef}
                        type="textarea"
                        rows={15}
                        value={text}
                        onChange={e => setText(e.target.value)}
                        name="text"
                        disabled={isSaving}
                        maxLength={INPUT_MAX_LENGTH_LARGE}
                        isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, "text")}
                    />
                }
                {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, "text") &&
                    <ValidationMessage>{validationErrors.text?.message}</ValidationMessage>
                }
            </div>
        </ConfirmModal>
    )
}

export default TextBuildingBlockModal
